import { Observable, from, defer, merge } from "rxjs"
import { switchMap, catchError, filter, debounceTime, delay, delayWhen } from "rxjs/operators"
import { ofType, StateObservable } from "redux-observable"
import type { EpicDependencies } from "../../../rxjs/configureEpics"
import { ACCOUNT_SELECTED, REFETCH_DATA, refetchData } from "../../../auth/actions"
import {
    createLoadingEnded,
    createLoadingEndedError,
    createLoadingStarted,
    SET_FILTER_VALUE,
    SET_FILTERS,
    SET_GLOBAL_DEFAULT_FILTER,
    SET_SUMMARY_REPORT_DEFAULT_FILTER,
    setSummaryReportDefaultFilter,
} from "../../../filters/actions"
import {
    fetchBreakdownByHourDataFailure,
    fetchBreakdownByHourDataSuccess,
    fetchNetCollectedBreakdownByPaymentTypeDataFailure,
    fetchNetCollectedBreakdownByPaymentTypeDataSuccess,
    fetchNetCollectedBreakdownDataFailure,
    fetchNetCollectedBreakdownDataSuccess,
    fetchNetCollectedByEmployeeDataFailure,
    fetchNetCollectedByEmployeeDataSuccess,
    fetchNetCollectedByEmployeeDetailDataFailure,
    fetchNetCollectedByEmployeeDetailDataSuccess,
    fetchNetCollectedBySourceDataFailure,
    fetchNetCollectedBySourceDataSuccess,
    fetchNetRevenueByAssignmentDataSuccess,
    fetchNetRevenueByCategoryDataFailure,
    fetchNetRevenueByCategoryDataSuccess,
    fetchTransactionsByPaymentTypeDataFailure,
    fetchTransactionsByPaymentTypeDataSuccess,
    fetchTrendingDataFailure,
    fetchTrendingDataSuccess,
} from "../../../../api/actions"
import { getHttpHeaders } from "../../../auth/selectors"
import { getSummaryGraphMode, getSummaryQueryApiParamNew, isSummaryReportAgendaOpened } from "./selectors"
import {
    clearSummaryData,
    SUMMARY_AGENDA_MOUNTED,
    SUMMARY_SELECT_ASSIGNEES_CLICKED,
    summaryAgendaMounted,
} from "./actions"
import { waitForAccountSelected, waitForGlobalFilterSet } from "../customers/customersTable/epics"
import { GLOBAL_FILTER_IDENTIFIER } from "../../../filters/reducer"
import { trendingIdentifier } from "./components/trending/contants"
import { breakdownByHourIdentifier } from "./components/breakdownByHour/contants"
import { SummaryGraphTypeEnum } from "./types"
import { areAnyLocationsSelected } from "../../../filters/selectors"
import {
    fetchNetCollectedBreakdownByEmployeeReportData,
    fetchNetCollectedBreakdownByPaymentTypeReportData,
    fetchNetCollectedBreakdownReportData,
    fetchNetCollectedByEmployeeReportData,
    fetchNetCollectedBySourceReportData,
    fetchNetRevenueByAssignmentReportData,
    fetchNetRevenueByCategoryReportData,
    fetchNetRevenueDailyReportData,
    fetchNetRevenueHourlyReportData,
    fetchTransactionsByPaymentTypeReportData,
} from "./request"
import {
    transformSummaryNetRevenueDailyItemToTrendingDataItem,
    transformSummaryNetRevenueHourlyItemToBreakdownByHourItem,
} from "./utils"
import { Action, StateObject } from "../../../redux/types"

export const summaryReportAccountSwitch = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ACCOUNT_SELECTED),
        filter(() => isSummaryReportAgendaOpened(deps.getState())),
        switchMap(() => [summaryAgendaMounted()])
    )

export const summaryAgendaMountedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SUMMARY_AGENDA_MOUNTED),
        delayWhen((action: Action) => waitForAccountSelected(actions$, deps.getState(), action)),
        delayWhen(action => waitForGlobalFilterSet(actions$, deps.getState(), action)),
        switchMap(() => [setSummaryReportDefaultFilter()])
    )

export const assigneeFilterChangedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SUMMARY_SELECT_ASSIGNEES_CLICKED),
        delay(100),
        switchMap(() => [refetchData()])
    )

export const fetchSummaryReportRequestedLoading = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    merge(
        actions$.pipe(ofType(SUMMARY_AGENDA_MOUNTED, REFETCH_DATA)),
        actions$.pipe(
            ofType(SET_FILTERS, SET_FILTER_VALUE),
            filter(
                (action: Action) =>
                    action.payload.identifier === "summaryReport" ||
                    action.payload.identifier === GLOBAL_FILTER_IDENTIFIER
            )
        )
    ).pipe(
        filter(() => isSummaryReportAgendaOpened(deps.getState())),
        switchMap(() => {
            if (areAnyLocationsSelected(deps.getState())) {
                return []
            } else {
                return [clearSummaryData()]
            }
        })
    )

export const summaryReportFilterChangedLoadings = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    merge(
        actions$.pipe(ofType(SET_SUMMARY_REPORT_DEFAULT_FILTER, SET_GLOBAL_DEFAULT_FILTER, REFETCH_DATA)),
        actions$.pipe(
            ofType(SET_FILTERS, SET_FILTER_VALUE),
            filter(
                (action: Action) =>
                    action.payload.identifier === "summaryReport" ||
                    action.payload.identifier === GLOBAL_FILTER_IDENTIFIER
            )
        )
    ).pipe(
        filter(() => isSummaryReportAgendaOpened(deps.getState())),
        switchMap(() => {
            if (areAnyLocationsSelected(deps.getState())) {
                return [
                    createLoadingStarted("netCollectedBreakdownDataLoading"),
                    createLoadingStarted("netCollectedBreakdownByPaymentTypeDataLoading"),
                    createLoadingStarted("netCollectedByEmployeeDataLoading"),
                    createLoadingStarted("netCollectedByEmployeeDetailDataLoading"),
                    createLoadingStarted("netRevenueByAssignmentDataLoading"),
                    createLoadingStarted("netRevenueByCategoryDataLoading"),
                    createLoadingStarted("transactionsByPaymentTypeDataLoading"),
                    createLoadingStarted("netCollectedBySourceDataLoading"),
                    getSummaryGraphMode(deps.getState()) === SummaryGraphTypeEnum.Daily
                        ? createLoadingStarted(trendingIdentifier)
                        : createLoadingStarted(breakdownByHourIdentifier),
                ]
            } else {
                return [
                    createLoadingEnded("netCollectedBreakdownDataLoading"),
                    createLoadingEnded("netCollectedBreakdownByPaymentTypeDataLoading"),
                    createLoadingEnded("transactionsByPaymentTypeDataLoading"),
                    createLoadingEnded("netCollectedByEmployeeDataLoading"),
                    createLoadingEnded("netCollectedByEmployeeDetailDataLoading"),
                    createLoadingEnded("netRevenueByCategoryDataLoading"),
                    createLoadingEnded("netRevenueByAssignmentDataLoading"),
                    createLoadingEnded("breakdownByHourIdentifier"),
                    createLoadingEnded("netCollectedBySourceDataLoading"),
                ]
            }
        })
    )

export const summaryReportFilterChanged = (
    actions$: Observable<Action>,
    state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    merge(
        actions$.pipe(ofType(SET_SUMMARY_REPORT_DEFAULT_FILTER, SET_GLOBAL_DEFAULT_FILTER, REFETCH_DATA)),
        actions$.pipe(
            ofType(SET_FILTERS, SET_FILTER_VALUE),
            filter(
                (action: Action) =>
                    action.payload.identifier === "summaryReport" ||
                    action.payload.identifier === GLOBAL_FILTER_IDENTIFIER
            )
        )
    ).pipe(
        filter(() => isSummaryReportAgendaOpened(deps.getState())),
        debounceTime(1000),
        switchMap(() => {
            if (areAnyLocationsSelected(deps.getState())) {
                return merge(
                    defer(() =>
                        fetchNetCollectedBreakdownReportData(
                            getHttpHeaders(deps.getState()),
                            getSummaryQueryApiParamNew(deps.getState())
                        )
                    ).pipe(
                        switchMap(result =>
                            result
                                ? [
                                      fetchNetCollectedBreakdownDataSuccess(result),
                                      createLoadingEnded("netCollectedBreakdownDataLoading"),
                                  ]
                                : [
                                      fetchNetCollectedBreakdownDataFailure(),
                                      createLoadingEnded("netCollectedBreakdownDataLoading"),
                                  ]
                        )
                    ),
                    defer(() =>
                        fetchNetCollectedBreakdownByPaymentTypeReportData(
                            getHttpHeaders(deps.getState()),
                            getSummaryQueryApiParamNew(deps.getState())
                        )
                    ).pipe(
                        switchMap(result => {
                            return result
                                ? [
                                      fetchNetCollectedBreakdownByPaymentTypeDataSuccess(result),
                                      createLoadingEnded("netCollectedBreakdownByPaymentTypeDataLoading"),
                                  ]
                                : [
                                      fetchNetCollectedBreakdownByPaymentTypeDataFailure(),
                                      createLoadingEnded("netCollectedBreakdownByPaymentTypeDataLoading"),
                                  ]
                        })
                    ),
                    defer(() =>
                        fetchTransactionsByPaymentTypeReportData(
                            getHttpHeaders(deps.getState()),
                            getSummaryQueryApiParamNew(deps.getState())
                        )
                    ).pipe(
                        switchMap(result =>
                            result
                                ? [
                                      fetchTransactionsByPaymentTypeDataSuccess(result),
                                      createLoadingEnded("transactionsByPaymentTypeDataLoading"),
                                  ]
                                : [
                                      fetchTransactionsByPaymentTypeDataFailure(),
                                      createLoadingEnded("transactionsByPaymentTypeDataLoading"),
                                  ]
                        )
                    ),
                    defer(() =>
                        fetchNetCollectedByEmployeeReportData(
                            getHttpHeaders(deps.getState()),
                            getSummaryQueryApiParamNew(deps.getState())
                        )
                    ).pipe(
                        switchMap(result =>
                            result
                                ? [
                                      fetchNetCollectedByEmployeeDataSuccess(result),
                                      createLoadingEnded("netCollectedByEmployeeDataLoading"),
                                  ]
                                : [
                                      fetchNetCollectedByEmployeeDataFailure(),
                                      createLoadingEnded("netCollectedByEmployeeDataLoading"),
                                  ]
                        )
                    ),
                    defer(() =>
                        fetchNetCollectedBreakdownByEmployeeReportData(
                            getHttpHeaders(deps.getState()),
                            getSummaryQueryApiParamNew(deps.getState())
                        )
                    ).pipe(
                        switchMap(result =>
                            result
                                ? [
                                      fetchNetCollectedByEmployeeDetailDataSuccess(result),
                                      createLoadingEnded("netCollectedByEmployeeDetailDataLoading"),
                                  ]
                                : [
                                      fetchNetCollectedByEmployeeDetailDataFailure(),
                                      createLoadingEnded("netCollectedByEmployeeDetailDataLoading"),
                                  ]
                        )
                    ),
                    defer(() =>
                        fetchNetRevenueByCategoryReportData(
                            getHttpHeaders(deps.getState()),
                            getSummaryQueryApiParamNew(deps.getState())
                        )
                    ).pipe(
                        switchMap(result =>
                            result
                                ? [
                                      fetchNetRevenueByCategoryDataSuccess(result),
                                      createLoadingEnded("netRevenueByCategoryDataLoading"),
                                  ]
                                : [
                                      fetchNetRevenueByCategoryDataFailure(),
                                      createLoadingEnded("netRevenueByCategoryDataLoading"),
                                  ]
                        )
                    ),
                    defer(() =>
                        fetchNetRevenueByAssignmentReportData(
                            getHttpHeaders(deps.getState()),
                            getSummaryQueryApiParamNew(deps.getState())
                        )
                    ).pipe(
                        switchMap(result =>
                            result
                                ? [
                                      fetchNetRevenueByAssignmentDataSuccess(result),
                                      createLoadingEnded("netRevenueByAssignmentDataLoading"),
                                  ]
                                : [
                                      fetchNetRevenueByCategoryDataFailure(),
                                      createLoadingEnded("netRevenueByAssignmentDataLoading"),
                                  ]
                        )
                    ),
                    defer(() =>
                        fetchNetCollectedBySourceReportData(
                            getHttpHeaders(deps.getState()),
                            getSummaryQueryApiParamNew(deps.getState())
                        )
                    ).pipe(
                        switchMap(result => {
                            return result
                                ? [
                                      fetchNetCollectedBySourceDataSuccess(result),
                                      createLoadingEnded("netCollectedBySourceDataLoading"),
                                  ]
                                : [
                                      // @ts-expect-error
                                      fetchNetCollectedBySourceDataFailure(),
                                      createLoadingEnded("netCollectedBySourceDataLoading"),
                                  ]
                        })
                    ),
                    getSummaryGraphMode(deps.getState()) === SummaryGraphTypeEnum.Daily
                        ? defer(() =>
                              fetchNetRevenueDailyReportData(
                                  getHttpHeaders(deps.getState()),
                                  getSummaryQueryApiParamNew(deps.getState())
                              )
                          ).pipe(
                              switchMap(result => {
                                  return result
                                      ? [
                                            fetchTrendingDataSuccess(
                                                transformSummaryNetRevenueDailyItemToTrendingDataItem(result)
                                            ),
                                            createLoadingEnded(breakdownByHourIdentifier),
                                            createLoadingEnded(trendingIdentifier),
                                        ]
                                      : [
                                            createLoadingEndedError(trendingIdentifier),
                                            // @ts-expect-error
                                            fetchTrendingDataFailure(error),
                                        ]
                              })
                          )
                        : defer(() =>
                              fetchNetRevenueHourlyReportData(
                                  getHttpHeaders(deps.getState()),
                                  getSummaryQueryApiParamNew(deps.getState())
                              )
                          ).pipe(
                              switchMap(response => [
                                  createLoadingEnded(breakdownByHourIdentifier),
                                  createLoadingEnded(trendingIdentifier),
                                  fetchBreakdownByHourDataSuccess(
                                      transformSummaryNetRevenueHourlyItemToBreakdownByHourItem(response)
                                  ),
                              ]),
                              catchError(error =>
                                  from([
                                      createLoadingEndedError(breakdownByHourIdentifier),
                                      fetchBreakdownByHourDataFailure(error),
                                  ])
                              )
                          )
                )
            } else {
                return [
                    createLoadingEnded("netCollectedBreakdownDataLoading"),
                    createLoadingEnded("netCollectedBreakdownByPaymentTypeDataLoading"),
                    createLoadingEnded("transactionsByPaymentTypeDataLoading"),
                    createLoadingEnded("netCollectedByEmployeeDataLoading"),
                    createLoadingEnded("netCollectedByEmployeeDetailDataLoading"),
                    createLoadingEnded("netRevenueByCategoryDataLoading"),
                    createLoadingEnded("netRevenueByAssignmentDataLoading"),
                    createLoadingEnded("breakdownByHourIdentifier"),
                    createLoadingEnded("netCollectedBySourceDataLoading"),
                ]
            }
        })
    )

export default [
    assigneeFilterChangedEpics,
    summaryReportFilterChangedLoadings,
    summaryReportFilterChanged,
    fetchSummaryReportRequestedLoading,
    summaryAgendaMountedEpics,
    summaryReportAccountSwitch,
]
